'use client';

import { ComponentType, useEffect, useState } from 'react';
import { Experience, ExperienceConfiguration } from '@ninetailed/experience.js-react';
import { PageSectionProps } from 'libs/contentful/props';
import { ModuleProps, ModuleTypeName } from 'utils/modules';
import { ContentfulModule } from 'libs/contentful/props';

export type NinetailedContentfulModule = ContentfulModule & {
  id: string;
};

interface ClientNinetailedExperienceProps {
  id: string;
  passthroughProps: PageSectionProps & ModuleProps[ModuleTypeName];
  module: ComponentType<PageSectionProps & ModuleProps[ModuleTypeName]>;
  experiences: ExperienceConfiguration<NinetailedContentfulModule>[];
  hasExperiments: boolean;
}

/**
 * Renders a client Ninetailed experience for a given component
 * https://www.contentful.com/developers/docs/personalization/experience-sdk/#render-experiences
 * @param id - The id of the component
 * @param passthroughProps - The passthrough props to render
 * @param component - The component to render
 * @param experiences - The experiences to render
 * @param hasExperiments - Whether the page has experiments
 * @returns The rendered component
 */
export const ClientNinetailedExperience = ({
  id,
  passthroughProps,
  module: Module,
  experiences,
  hasExperiments,
}: ClientNinetailedExperienceProps) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to ensure preview plugin is ready
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  if (!hasExperiments) {
    return <Module {...(passthroughProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])} />;
  }

  // Handle the variant data within the experiences (GraphQL data doesn't need flattening)
  const processedExperiences = experiences.map(experience => {
    return {
      ...experience,
      components: experience.components?.map(component => ({
        ...component,
        variants: component.variants?.map(variant => ({
          ...variant,
        })),
      })),
    };
  }) as typeof experiences;

  // Show baseline until ready
  if (!isReady) {
    return <Module {...(passthroughProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])} />;
  }

  return (
    <Experience
      {...(passthroughProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])}
      id={id}
      component={Module}
      experiences={processedExperiences}
    />
  );
};
